/**
 * The chart. The app's home screen.
 *
 * Shows the anchorage registry, this vessel's own applications, and whatever
 * other vessels the server decided this captain may see. Long-press (or
 * right-click) drops a pin and offers to apply there; tapping a registry
 * anchorage offers to apply at that one.
 *
 * All three datasets go through `cached()`, so a cold start with no signal
 * shows yesterday's chart rather than an empty sea -- labelled stale, because a
 * captain acting on old occupancy data without knowing it is old is worse off
 * than one looking at a spinner.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner, formatPosition } from '@abg/ui'
import { relative } from '@abg/ui/time'
import { MapView } from '../components/MapView'
import { DepthPanel } from '../components/DepthPanel'
import { UsageSummary } from '../components/UsageSummary'
import { api } from '../lib/api'
import { cached } from '../lib/offline'
import { getFix, GeolocationFailed } from '../lib/geo'
import type { Anchorage, Application, Neighbour } from '../lib/types'

export function MapPage() {
  const navigate = useNavigate()
  const [anchorages, setAnchorages] = useState<Anchorage[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [neighbours, setNeighbours] = useState<Neighbour[]>([])
  const [loading, setLoading] = useState(true)
  const [stale, setStale] = useState<number | null>(null)
  const [pin, setPin] = useState<{ lat: number; lon: number } | null>(null)
  const [selected, setSelected] = useState<Anchorage | null>(null)
  const [center, setCenter] = useState<[number, number] | undefined>()
  const [locating, setLocating] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const [a, m, n] = await Promise.all([
        cached('anchorages', () => api.get<Anchorage[]>('/api/anchorages')),
        cached('applications', () => api.get<Application[]>('/api/applications')),
        // Neighbours are the one dataset that is genuinely time-sensitive, but
        // it is cached too: a stale neighbour list marked stale beats none.
        cached('neighbours', () => api.get<Neighbour[]>('/api/neighbours')),
      ])
      if (cancelled) return
      setAnchorages(a.data)
      setApplications(m.data)
      setNeighbours(n.data)
      const anyStale = [a, m, n].filter((r) => r.stale)
      setStale(anyStale.length ? Math.min(...anyStale.map((r) => r.at)) : null)
      setLoading(false)
    })().catch(() => setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const live = useMemo(
    () =>
      applications.filter((a) =>
        ['submitted', 'with_csc', 'approved', 'active'].includes(a.state),
      ),
    [applications],
  )

  const locate = useCallback(async () => {
    setLocating(true)
    setGeoError(null)
    try {
      const fix = await getFix()
      setCenter([fix.lat, fix.lon])
      setPin({ lat: fix.lat, lon: fix.lon })
    } catch (error) {
      setGeoError(error instanceof GeolocationFailed ? error.message : 'Could not get a fix.')
    } finally {
      setLocating(false)
    }
  }, [])

  const applyHere = () => {
    if (selected) {
      navigate(`/apply?anchorage=${selected.id}`)
    } else if (pin) {
      navigate(`/apply?lat=${pin.lat.toFixed(6)}&lon=${pin.lon.toFixed(6)}`)
    }
  }

  return (
    <div className="map-page page-map">
      <MapView
        anchorages={anchorages}
        applications={live}
        neighbours={neighbours}
        pin={pin}
        center={center}
        zoom={center ? 13 : undefined}
        onPick={(lat, lon) => {
          setSelected(null)
          setPin({ lat, lon })
        }}
        onAnchorageClick={(anchorage) => {
          setPin(null)
          setSelected(anchorage)
        }}
      />

      <div className="map-legend">
        {loading ? (
          <Spinner label="Loading chart…" />
        ) : (
          <>
            <div className="row">
              <span className="swatch" style={{ color: 'var(--sea-green)' }} />
              <span>Anchorage</span>
            </div>
            <div className="row">
              <span className="swatch" style={{ color: 'var(--abg-navy)' }} />
              <span>Mine</span>
            </div>
            <div className="row">
              <span className="swatch" style={{ color: 'var(--abg-signal)' }} />
              <span>Friends</span>
            </div>
            <div className="row">
              <span className="swatch" style={{ color: 'var(--abg-grey-mid)' }} />
              <span>Others sharing</span>
            </div>
            {stale && (
              <div className="caption" style={{ marginTop: 'var(--space-1)' }}>
                Saved data, {relative(new Date(stale).toISOString())}
              </div>
            )}
            <div className="caption" style={{ marginTop: 'var(--space-1)' }}>
              Long-press the chart to propose a position.
            </div>
          </>
        )}
      </div>

      {(pin || selected) && (
        <div className="sheet">
          {selected ? (
            <>
              <div className="row-between">
                <strong>{selected.name ?? 'Unnamed anchorage'}</strong>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => setSelected(null)}
                >
                  Close
                </button>
              </div>
              <div className="mono small">{formatPosition(selected.lat, selected.lon)}</div>
              {selected.municipality && <div className="small muted">{selected.municipality}</div>}
              {selected.depth_notes && <div className="small">{selected.depth_notes}</div>}
              {selected.holding_notes && <div className="small">{selected.holding_notes}</div>}
              {selected.restrictions && (
                <div className="small">
                  <em>{selected.restrictions}</em>
                </div>
              )}
              <DepthPanel
                anchorage={selected}
                onReported={(reported) => {
                  // Reflected straight into the sheet so a captain sees their
                  // own contribution land, rather than after a refetch.
                  setSelected({ ...selected, reported_depth: reported })
                }}
              />
              <UsageSummary anchorageId={selected.id} />
              {selected.status !== 'active' && (
                <div className={selected.status === 'blocked' ? 'notice notice-alert' : 'notice notice-warn'}>
                  <strong>
                    {selected.status === 'blocked' ? 'Blocked.' : 'Discouraged.'}
                  </strong>{' '}
                  {selected.status_reason ?? 'Speak to us before applying.'}
                </div>
              )}
              <button
                type="button"
                className="btn btn-lg btn-block"
                onClick={applyHere}
                disabled={selected.status === 'blocked'}
              >
                {selected.status === 'blocked' ? 'Cannot apply here' : 'Apply to anchor here'}
              </button>
            </>
          ) : (
            <>
              <div className="row-between">
                <strong>New position</strong>
                <button type="button" className="btn btn-quiet btn-sm" onClick={() => setPin(null)}>
                  Close
                </button>
              </div>
              <div className="mono small">{pin && formatPosition(pin.lat, pin.lon)}</div>
              <p className="caption">
                Not a registered anchorage. We will check it with the local harbour authority;
                if approved, it is added to the registry for next time.
              </p>
              <button type="button" className="btn btn-lg btn-block" onClick={applyHere}>
                Apply to anchor here
              </button>
            </>
          )}
        </div>
      )}

      {!pin && !selected && (
        <div className="map-actions">
          <button type="button" className="btn btn-quiet" onClick={locate} disabled={locating}>
            {locating ? 'Locating…' : 'My position'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/apply')}>
            New application
          </button>
        </div>
      )}

      {geoError && (
        <div className="sheet" role="alert">
          <strong>Position unavailable</strong>
          <p className="small">{geoError}</p>
          <button type="button" className="btn btn-quiet" onClick={() => setGeoError(null)}>
            Dismiss
          </button>
        </div>
      )}
    </div>
  )
}
